import { api } from "./axiosInstance";
import type { Role } from "../types/domain.types";

export interface AdminUser {
  id: string;
  email: string;
  role: Role;
  isActive: boolean;
  createdAt: string;
}

export async function listUsersRequest(): Promise<AdminUser[]> {
  const { data } = await api.get<AdminUser[]>("/admin/users");
  return data;
}

export interface CreateUserInput {
  email: string;
  password: string;
  role: Role;
}

export async function createUserRequest(
  input: CreateUserInput,
): Promise<{ id: string; email: string; role: Role }> {
  const { data } = await api.post("/admin/users", input);
  return data;
}

export interface UpdateUserInput {
  role?: Role;
  isActive?: boolean;
}

export async function updateUserRequest(
  id: string,
  input: UpdateUserInput,
): Promise<{ id: string; role: Role; isActive: boolean }> {
  const { data } = await api.patch(`/admin/users/${id}`, input);
  return data;
}

export interface UploadPlanInput {
  name: string;
  clientName: string;
  tilesZip: File;
  onProgress?: (pct: number) => void;
}

export interface UploadPlanResponse {
  id: string;
  name: string;
  clientName: string;
  status: "processing" | "completed" | "failed";
}

export interface UploadStatusResponse {
  id: string;
  name: string;
  clientName: string;
  status: "processing" | "completed" | "failed";
  totalTiles: number;
  uploadedTiles: number;
  error?: string;
}

export async function getUploadStatusRequest(
  id: string,
): Promise<UploadStatusResponse> {
  const { data } = await api.get<UploadStatusResponse>(
    `/admin/plans/upload-status/${id}`,
  );
  return data;
}

export async function uploadPlanRequest({
  name,
  clientName,
  tilesZip,
  onProgress,
}: UploadPlanInput): Promise<UploadPlanResponse> {
  const form = new FormData();
  form.append("name", name);
  form.append("clientName", clientName);
  form.append("tilesZip", tilesZip);

  const { data } = await api.post<UploadPlanResponse>(
    "/admin/plans/upload",
    form,
    {
      // Axios detecta FormData y fija Content-Type con boundary correcto.
      onUploadProgress: (evt) => {
        if (!onProgress || !evt.total) return;
        const pct = Math.round((evt.loaded * 100) / evt.total);
        onProgress(pct);
      },
      timeout: 10 * 60 * 1000, // 10 min para ZIPs grandes
    },
  );
  return data;
}

export async function deletePlanRequest(id: string): Promise<void> {
  await api.delete(`/admin/plans/${id}`);
}

export interface ReplacePlanTilesInput {
  planId: string;
  tilesZip: File;
  onProgress?: (pct: number) => void;
}

export interface ReplacePlanTilesResponse {
  id: string;
  name: string;
  clientName: string;
  status: "processing" | "completed" | "failed";
}

export async function replacePlanTilesRequest({
  planId,
  tilesZip,
  onProgress,
}: ReplacePlanTilesInput): Promise<ReplacePlanTilesResponse> {
  const form = new FormData();
  form.append("tilesZip", tilesZip);

  const { data } = await api.post<ReplacePlanTilesResponse>(
    `/admin/plans/${planId}/replace-tiles`,
    form,
    {
      onUploadProgress: (evt) => {
        if (!onProgress || !evt.total) return;
        const pct = Math.round((evt.loaded * 100) / evt.total);
        onProgress(pct);
      },
      timeout: 10 * 60 * 1000,
    },
  );
  return data;
}
